import axios from "axios";
import {
  AI_VOICE_PROCESS_URL,
  BASE_URL,
} from "../Constants";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const getAudioBaseUrl = () => trimTrailingSlash(BASE_URL);

const readFirstString = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
};

const readFirstBoolean = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "boolean") return value;
      if (typeof value === "string" && value.trim()) {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(normalized)) return true;
        if (["false", "0", "no"].includes(normalized)) return false;
      }
    }
  }

  return false;
};

const normalizeAudioSource = (value, mimeType) => {
  if (typeof value !== "string" || !value.trim()) return "";

  const cleanValue = value.trim();

  if (/^(https?:|data:|blob:)/i.test(cleanValue)) return cleanValue;

  if (cleanValue.startsWith("/")) {
    return `${getAudioBaseUrl()}${cleanValue}`;
  }

  const compact = cleanValue.replace(/\s/g, "");
  const looksLikeBase64 = compact.length > 96 && /^[A-Za-z0-9+/=]+$/.test(compact);

  if (looksLikeBase64) {
    return `data:${mimeType || "audio/mpeg"};base64,${compact}`;
  }

  return "";
};

const getAudioExtension = (mimeType) => {
  const type = String(mimeType || "").toLowerCase();
  if (type.includes("mp4")) return "m4a";
  if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
  if (type.includes("wav")) return "wav";
  if (type.includes("ogg")) return "ogg";
  return "webm";
};

export const normalizeVoiceAssistantResponse = (payload) => {
  if (typeof payload === "string") {
    return {
      transcript: "",
      responseText: payload.trim(),
      sessionId: "",
      needsClarification: false,
      audioSource: "",
      raw: payload,
    };
  }

  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const nested = data.data && typeof data.data === "object" ? data.data : data;
  const result = data.result && typeof data.result === "object" ? data.result : {};
  const objects = [nested, result, data, root];

  const transcript = readFirstString(objects, [
    "transcript",
    "user_transcript",
    "user_text",
    "input_text",
    "recognized_text",
    "speech_text",
    "request.transcript",
    "request.text",
  ]);

  const sessionId = readFirstString(objects, ["session_id", "sessionId", "conversation_id"]);
  const audioValue = readFirstString(objects, [
    "audio_url",
    "audioUrl",
    "response_audio_url",
    "audio",
    "audio_base64",
    "response_audio",
  ]);
  const audioMimeType = readFirstString(objects, ["audio_mime_type", "audioMimeType", "mime_type"]);

  const rawResponseText = readFirstString(objects, [
    "assistant_response",
    "assistant_text",
    "response_text",
    "reply",
    "answer",
    "response",
    "output_text",
    "result_text",
    "message",
  ]);
  const normalizedResponseText = rawResponseText.toLowerCase();
  const responseText =
    ["ok", "success"].includes(normalizedResponseText) && (transcript || audioValue)
      ? ""
      : rawResponseText;

  const needsClarification =
    readFirstBoolean(objects, [
      "needs_clarification",
      "requires_clarification",
      "clarification_required",
      "needs_clear",
      "requires_clear",
      "clear_required",
      "need_clear",
    ]) ||
    ["clarification_required", "needs_clarification", "requires_clear"].includes(
      String(readFirstString(objects, ["status", "intent", "type", "stage"])).toLowerCase()
    );
  const candidatesSource =
    objects.find((object) => Array.isArray(object?.candidates))?.candidates || [];
  const candidates = candidatesSource.filter(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  const clarificationReason = readFirstString(objects, ["reason", "clarification_reason"]);
  const requestId = readFirstString(objects, ["request_id", "requestId"]);

  return {
    transcript,
    responseText,
    sessionId,
    needsClarification,
    clarification: needsClarification
      ? {
          candidates,
          reason: clarificationReason,
          requestId,
        }
      : null,
    audioSource: normalizeAudioSource(audioValue, audioMimeType),
    raw: payload,
  };
};

export async function processVoiceCommand({
  audioBlob,
  sessionId,
  language,
  organizationId,
  signal,
}) {
  if (!audioBlob) {
    throw new Error("No audio recording was found.");
  }

  if (!organizationId) {
    throw new Error("Choose an organization before using the AI voice assistant.");
  }

  const formData = new FormData();
  const fileName = `voice-message.${getAudioExtension(audioBlob.type)}`;

  formData.append("audio", audioBlob, fileName);
  formData.append("session_id", sessionId || "");
  formData.append("language", language || "en");
  formData.append("org_id", String(organizationId));

  const response = await axios.post(AI_VOICE_PROCESS_URL, formData, {
    signal,
    withCredentials: true,
    headers: {
      Accept: "application/json",
    },
  });

  return normalizeVoiceAssistantResponse(response.data);
}
